import apiService from '../services/apiService';
import Task from '../models/Task';

export const taskController = {
  // Get all tasks with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `tasks?${queryParams}` : 'tasks';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => Task.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch tasks' };
    }
  },

  // Get single task by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`tasks/${id}`);
      return { success: true, data: Task.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get tasks by project
  getByProject: async (projectId) => {
    try {
      const response = await apiService.get(`tasks?projectId=${projectId}`);
      const items = response.data?.map(item => Task.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Get tasks by assignee
  getByAssignee: async (userId) => {
    try {
      const response = await apiService.get(`tasks?assignedTo=${userId}`);
      const items = response.data?.map(item => Task.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Create new task
  create: async (data) => {
    try {
      const item = new Task(data);
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.post('tasks', item.toJSON());
      return { 
        success: true, 
        data: Task.fromJSON(response.data), 
        message: 'Task created successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing task
  update: async (id, data) => {
    try {
      const item = new Task({ ...data, id });
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.put(`tasks/${id}`, item.toJSON());
      return { 
        success: true, 
        data: Task.fromJSON(response.data), 
        message: 'Task updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update task status
  updateStatus: async (id, status) => {
    try {
      const updateData = { status };
      if (status === 'Completed') {
        updateData.completedDate = new Date().toISOString();
        updateData.progress = 100;
      }
      const response = await apiService.patch(`tasks/${id}/status`, updateData);
      return { 
        success: true, 
        data: Task.fromJSON(response.data), 
        message: 'Task status updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update task progress
  updateProgress: async (id, progress) => {
    try {
      const response = await apiService.patch(`tasks/${id}/progress`, { progress });
      return { 
        success: true, 
        data: Task.fromJSON(response.data), 
        message: 'Progress updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete task
  delete: async (id) => {
    try {
      await apiService.delete(`tasks/${id}`);
      return { success: true, message: 'Task deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk update tasks
  bulkUpdate: async (taskIds, updates) => {
    try {
      const response = await apiService.post('tasks/bulk-update', { taskIds, updates });
      return { success: true, data: response.data, message: 'Tasks updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk delete tasks
  bulkDelete: async (taskIds) => {
    try {
      await apiService.post('tasks/bulk-delete', { taskIds });
      return { success: true, message: 'Tasks deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Export tasks
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`tasks/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get task statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `tasks/statistics?${queryParams}` : 'tasks/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Add comment to task
  addComment: async (id, comment) => {
    try {
      const response = await apiService.post(`tasks/${id}/comments`, { comment });
      return { success: true, data: response.data, message: 'Comment added' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }
};

export default taskController;